import { filesApi } from '../files/filesApi';
import { clearAccessToken } from '../msalAccount/msalAccountSlice';
import { graphClient } from '@/utils/msGraphClient';

export const UPLOAD_CHUNK_SIZE = 8 * 1024 * 1024;

export const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB

// OData string literals are single-quoted; a literal quote inside the value must be
// escaped by doubling it, otherwise it terminates the string early (e.g. name eq 'a'b'').
// The result is also URL-encoded since callers always splice it straight into a URL/query
// string, and unencoded characters like '&' would otherwise be parsed as part of the URL.
export const escapeODataString = (value) => encodeURIComponent(String(value).replace(/'/g, "''"));

// $search phrase queries are double-quoted (e.g. search(q="term")); a literal backslash or
// quote inside the value must be backslash-escaped, otherwise it terminates the phrase early.
// The result is also URL-encoded for the same reason as escapeODataString above.
export const escapeODataSearchPhrase = (value) =>
	encodeURIComponent(String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"'));

export const getError = (error, dispatch) => {
	console.log('ERROR', error);
	const code = error?.response?.data?.error?.code ?? error?.code;
	console.log('ERROR CODE', code);
	if ((code ?? '').toString() === 'InvalidAuthenticationToken') {
		dispatch(clearAccessToken());
	}
	return {
		error: {
			code: code ?? error?.code,
			message: error.response?.data?.error?.message ?? error.message ?? '',
			statusCode: error?.statusCode ?? error?.status ?? error?.response?.status,
		},
	};
};

const NO_PERMISSION = {
	error: 'SharePoint access denied',
	errorMessage:
		'You do not have permission to view this SharePoint folder. Ask your admin to add you to the SharePoint library.',
};
const NOT_FOUND = {
	error: 'SharePoint access denied or not found',
	errorMessage:
		'You may not have permission to view this folder, or it may no longer exist. Ask your admin if you need access.',
};
const INVALID_REQUEST = {
	error: 'SharePoint folder invalid',
	errorMessage:
		'SharePoint could not load this folder. The saved drive or folder ID may be invalid.',
};
const FOLDER_ALREADY_EXISTS = {
	error: 'Folder already exists',
	errorMessage: 'A folder with this name already exists in this location.',
};
const DEFAULT_SHAREPOINT_ERROR = {
	error: 'SharePoint folder failed to load',
	errorMessage:
		'Error getting SharePoint folder. Please try again or check the saved drive and folder IDs.',
};
export const UPLOAD_SHAREPOINT_ERROR = {
	error: 'SharePoint upload failed',
	errorMessage:
		'The file could not be uploaded to SharePoint. Check your connection and try again.',
};

const PATH_TOO_LONG = {
	error: 'SharePoint path is too long',
	errorMessage:
		'The file name or folder path is too long for SharePoint, try a shorter file name.',
};
const SHAREPOINT_LOAD_ERRORS = {
	accessdenied: NO_PERMISSION,
	forbidden: NO_PERMISSION,
	itemnotfound: NOT_FOUND,
	notfound: NOT_FOUND,
	invalidrequest: INVALID_REQUEST,
	namealreadyexists: FOLDER_ALREADY_EXISTS,
	pathistoolong: PATH_TOO_LONG,
};

export const getSharepointError = (error, fallback = DEFAULT_SHAREPOINT_ERROR) => {
	if (!error) return null;

	const code = String(error.code ?? error.data?.code ?? '').toLowerCase();
	const status = error.statusCode ?? error.status ?? error.data?.statusCode;

	if (SHAREPOINT_LOAD_ERRORS[code]) {
		return SHAREPOINT_LOAD_ERRORS[code];
	}

	if (status === 401 || status === 403) {
		return SHAREPOINT_LOAD_ERRORS.forbidden;
	}
	if (status === 404) {
		return SHAREPOINT_LOAD_ERRORS.itemnotfound;
	}
	if (status === 409) {
		return SHAREPOINT_LOAD_ERRORS.namealreadyexists;
	}
	return fallback;
};

export const invalidateFilesTags = async (dispatch, { driveId, itemId }) => {
	try {
		await dispatch(
			filesApi.util.invalidateTags([{ type: 'FILES_LIST', id: `${driveId}-${itemId}` }]),
		);
	} catch (e) {
		console.log('Error invalidating Files API tags', e);
	}
};

export const encodeSharingUrl = (url) => {
	const base64Url = btoa(url).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
	return `u!${base64Url}`;
};

// const convertFileToBase64 = (file) =>
// 	new Promise((resolve, reject) => {
// 		const reader = new FileReader();
// 		reader.onload = () => resolve(reader.result);
// 		reader.onerror = reject;
// 		reader.readAsDataURL(file);
// 	});

export const convertFileToBase64 = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = () => resolve(reader.result);

		reader.onerror = () => {
			reject(
				new Error(
					`Unable to read file "${file.name}". It may be open, locked, or too large.`,
				),
			);
		};

		reader.readAsDataURL(file);
	});

export const convertFileToGraphAttachment = async (file) => {
	// if (file.size > MAX_FILE_SIZE) {
	// 	throw new Error(
	// 		`${file.name} is too large to attach to an email. Please upload it as a link instead.`,
	// 	);
	// }
	const base64 = await convertFileToBase64(file);
	return {
		'@odata.type': '#microsoft.graph.fileAttachment',
		name: file.name,
		contentType: file.type,
		contentBytes: base64.split(',')[1],
	};
};

const hasValue = (value) => {
	if (Array.isArray(value)) return value.length > 0;
	return value !== undefined && value !== null && value !== '';
};

const compactObject = (object) =>
	Object.fromEntries(Object.entries(object).filter(([, value]) => hasValue(value)));

const toStringValue = (value) => {
	if (value === undefined || value === null) return undefined;
	if (Array.isArray(value)) {
		return value.map(toStringValue).filter(Boolean).join('; ');
	}

	if (typeof value === 'object') return undefined;

	const stringValue = String(value).trim();
	return stringValue || undefined;
};

const firstStringValue = (...values) => values.map(toStringValue).find(Boolean);

const getContactEmails = (value) => {
	if (!value) return [];
	if (Array.isArray(value)) {
		return value
			.flatMap((item) => {
				if (typeof item === 'string') return [item];
				return [item?.contactEmail, item?.email, item?.emailAddress?.address, item?.name];
			})
			.map(toStringValue)
			.filter(Boolean);
	}

	return String(value).split(',').map(toStringValue).filter(Boolean);
};

export const getGraphRecipientEmails = (recipients = []) =>
	(recipients ?? [])
		.map((recipient) => recipient?.emailAddress?.address)
		.map(toStringValue)
		.filter(Boolean);

export const getInternetMessageHeader = (headers = [], headerName) => {
	const normalizedHeaderName = String(headerName ?? '').toLowerCase();
	return headers.find(
		(header) => String(header?.name ?? '').toLowerCase() === normalizedHeaderName,
	)?.value;
};

const getReferencesHeaderValues = (headers = []) =>
	(getInternetMessageHeader(headers, 'References') ?? '')
		.split(' ')
		.map(toStringValue)
		.filter(Boolean);

export const buildSentEmailResult = ({
	draftMessage = {},
	sentMessage = {},
	message = {},
	graphUser = {},
	mailboxEmail,
	senderEmail,
	sentAt,
}) => {
	const messageHeaders =
		sentMessage?.internetMessageHeaders ?? draftMessage?.internetMessageHeaders ?? [];
	const graphMessageId = firstStringValue(sentMessage?.id, draftMessage?.id);
	const fromEmail = firstStringValue(
		sentMessage?.from?.emailAddress?.address,
		message?.from?.emailAddress?.address,
		senderEmail,
		mailboxEmail,
	);

	return compactObject({
		messageId: graphMessageId,
		graphMessageId,
		draftMessageId: draftMessage?.id,
		graphUserId: graphUser?.id,
		mailboxEmail,
		internetMessageId: firstStringValue(
			sentMessage?.internetMessageId,
			draftMessage?.internetMessageId,
		),
		conversationId: firstStringValue(sentMessage?.conversationId, draftMessage?.conversationId),
		sentDateTime: firstStringValue(
			sentMessage?.sentDateTime,
			draftMessage?.sentDateTime,
			sentAt,
		),
		subject: firstStringValue(sentMessage?.subject, draftMessage?.subject, message?.subject),
		fromEmail,
		toEmails: getGraphRecipientEmails(sentMessage?.toRecipients ?? message?.toRecipients),
		ccEmails: getGraphRecipientEmails(sentMessage?.ccRecipients ?? message?.ccRecipients),
		bccEmails: getGraphRecipientEmails(sentMessage?.bccRecipients ?? message?.bccRecipients),
		bodyPreview: firstStringValue(sentMessage?.bodyPreview, draftMessage?.bodyPreview),
		inReplyTo: firstStringValue(getInternetMessageHeader(messageHeaders, 'In-Reply-To')),
		references: getReferencesHeaderValues(messageHeaders),
	});
};

const getWorkspaceContext = ({ values = {} }) => {
	const deal = values.deal ?? {};
	const client = values.client ?? {};
	const dealId = firstStringValue(deal?.dealId, values.dealId);
	const clientId = firstStringValue(
		client?.clientId,
		client?.id,
		values.clientId,
	);

	if (dealId) {
		return {
			workspaceId: dealId,
			workspaceType: 'deal',
			entityId: dealId,
			entityType: 'deal',
		};
	}

	if (clientId) {
		return {
			workspaceId: clientId,
			workspaceType: 'client',
			entityId: clientId,
			entityType: 'client',
		};
	}

	return {};
};

/** Folder path label for save-email / notes pickers. Root sites have an empty clientSiteSuffix, so split on the domain. */
export const getDriveItemFolderDisplayPath = (driveItem, folderOwner, userDetails) => {
	const suffix = folderOwner?.clientSiteSuffix ?? userDetails?.clientSiteSuffix;
	const domain = folderOwner?.sharepointSite ?? userDetails?.sharepointSite;
	const path = driveItem.webUrl.split(suffix || domain).pop();
	try {
		return decodeURI(path.normalize());
	} catch {
		return path;
	}
};

export const buildOutboundEmailSharepointMetadata = ({
	sendResult = {},
	values = {},
	userDetails = {},
}) => {
	const workspaceContext = getWorkspaceContext({ values });
	const toEmails = sendResult?.toEmails?.length
		? sendResult.toEmails
		: getContactEmails(values.recipient ?? values.to);
	const ccEmails = sendResult?.ccEmails?.length
		? sendResult.ccEmails
		: getContactEmails(values.cc);

	const metadata = {
		FolioInternetMessageId: sendResult?.internetMessageId,
		FolioConversationId: sendResult?.conversationId,
		FolioGraphMessageId: firstStringValue(sendResult?.graphMessageId, sendResult?.messageId),
		FolioSentDateTime: sendResult?.sentDateTime,
		FolioFromEmail: firstStringValue(
			sendResult?.fromEmail,
			values.from?.name,
			values.from?.email,
			values.from?.emailAddress,
			values.from,
			sendResult?.mailboxEmail,
		),
		FolioToEmails: toEmails,
		FolioCcEmails: ccEmails,
		FolioSubject: firstStringValue(sendResult?.subject, values.subject),
		FolioBrokerageId: firstStringValue(
			userDetails?.broker?.ledger,
			userDetails?.brokerGroupId,
			userDetails?.broker?.id,
		),
		FolioWorkspaceId: workspaceContext.workspaceId,
		FolioWorkspaceType: workspaceContext.workspaceType,
		FolioEntityId: workspaceContext.entityId,
		FolioEntityType: workspaceContext.entityType,
	};

	return Object.fromEntries(
		Object.entries(metadata)
			.map(([key, value]) => [key, toStringValue(value)])
			.filter(([, value]) => Boolean(value)),
	);
};

export const getFileExtension = (fileName) => {
	if (!fileName || typeof fileName !== 'string') return null;

	const extensionIndex = fileName.lastIndexOf('.');
	if (extensionIndex <= 0 || extensionIndex === fileName.length - 1) return null;

	return fileName.slice(extensionIndex + 1).toLowerCase();
};

export const normalizeAllowedFileTypes = (types) => {
	if (!Array.isArray(types) || types.length === 0) return null;

	return types.map((type) => String(type).toLowerCase()).filter(Boolean);
};

export const isFileExtensionAllowed = (fileName, types) => {
	const allowedTypes = normalizeAllowedFileTypes(types);
	if (!allowedTypes) return true;

	const extension = getFileExtension(fileName);
	if (!extension) return false;

	return allowedTypes.includes(extension);
};

export const getUniqueFileName = (files, originalName) => {
	let counter = 0;
	let fileName = originalName;
	const extensionIndex = originalName.lastIndexOf('.');
	const nameWithoutExtension =
		extensionIndex > 0 ? originalName.substring(0, extensionIndex) : originalName;
	const extension = extensionIndex > 0 ? originalName.substring(extensionIndex) : '';

	while (files.some((f) => f.name === fileName)) {
		counter++;
		fileName = `${nameWithoutExtension}(${counter})${extension}`;
	}

	return fileName;
};

//Works with firebase storage and ms files, converting them into file objects
export const convertFilesForUpload = async (files = []) => {
	if (!Array.isArray(files) || files.length === 0) return [];

	const results = await Promise.all(
		files.map(async (file) => {
			try {
				if (file instanceof File) {
					return {
						file: file,
						isSharepointFile: false,
					};
				}
				let blob = null;
				let sharepointFile = false;
				if (file?.driveId && file?.id) {
					sharepointFile = true;
					blob = await graphClient
						.api(`/drives/${file.driveId}/items/${file.id}/content`)
						.responseType('blob')
						.get();
				} else if (file?.downloadUrl) {
					const response = await fetch(file.downloadUrl, {});

					if (!response.ok) {
						throw new Error(`Failed downloading file: ${response.status}`);
					}

					blob = await response.blob();
				} else {
					console.warn('Unsupported file object:', file);
					return null;
				}

				if (!(blob instanceof Blob)) {
					console.warn('Invalid blob returned:', file);
					return null;
				}

				const filename = file.name || file.originalName || file.fileName;
				const mimeType = blob.type || file.mimeType || file.type;
				const fileObj = new File([blob], filename, { type: mimeType });
				return {
					file: fileObj,
					isSharepointFile: sharepointFile,
				};
			} catch (err) {
				console.error('❌ Failed converting file:', file, err);
				return null;
			}
		}),
	);

	return results.filter(Boolean);
};

export const buildBatchRequests = ({ driveId, chunk, itemIdByPath, itemId }) => {
	return chunk.map((folder, index) => {
		const requestId = `req_${index + 1}`;
		const parentItemId = folder.parentPath ? itemIdByPath.get(folder.parentPath) : itemId;

		let formattedFolderName;
		try {
			formattedFolderName = decodeURI(folder.name.normalize());
		} catch (e) {
			console.log('Error decoding uri', e);
			formattedFolderName = folder.name.normalize();
		}

		return {
			id: requestId,
			method: 'POST',
			url: `/drives/${driveId}/items/${parentItemId}/children`,
			headers: {
				'Content-Type': 'application/json',
			},
			body: {
				name: formattedFolderName,
				folder: {},
				'@microsoft.graph.conflictBehavior': 'replace',
			},
		};
	});
};

export const buildListItemBatchRequests = ({ driveId, items }) =>
	items.map((item, index) => ({
		id: `req_${index + 1}`,
		method: 'GET',
		url: `/drives/${driveId}/items/${item.id}/listItem`,
	}));

export const buildUploadSessionBatchRequests = ({ driveId, itemId, files }) =>
	files.map((entry, index) => ({
		id: `req_${index + 1}`,
		method: 'POST',
		url: `/drives/${driveId}/items/${itemId}:/${encodeURIComponent(entry.file.name)}:/createUploadSession`,
		headers: {
			'Content-Type': 'application/json',
		},
		body: {
			item: {
				'@microsoft.graph.conflictBehavior': 'rename',
				name: entry.file.name,
			},
		},
	}));

export const prepareFileForUpload = async (fileEntry) => {
	const file = fileEntry?.file ?? fileEntry;
	const metadata = fileEntry?.metadata ?? {};
	const fileContent = file?.content instanceof Blob ? file.content : await file.arrayBuffer();
	const fileSize = fileContent.byteLength ?? fileContent.size ?? file.size;

	return { file, metadata, fileContent, fileSize };
};

export const uploadFileContentToSession = async ({ uploadUrl, fileContent, fileSize }) => {
	let start = 0;
	let fileRes;

	while (start < fileSize) {
		const end = Math.min(start + UPLOAD_CHUNK_SIZE, fileSize);
		const contentChunk = fileContent.slice(start, end);

		fileRes = await graphClient
			.api(uploadUrl)
			.header('Content-Range', `bytes ${start}-${end - 1}/${fileSize}`)
			.putStream(contentChunk);
		start = end;
	}

	return fileRes;
};
